/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Payment Orders Full Audit Log & Transaction Ledger (Task 6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Receipt,
  Search,
  RefreshCw,
  Download,
  CheckCircle2,
  Clock,
  XCircle,
  DollarSign,
  Building2,
  CreditCard,
  Globe,
  ShieldCheck,
  Copy,
  Check,
  Eye,
  X,
  ExternalLink,
  FileText,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef, exportToCsv } from '../components/table/DataTable';
import type {
  AdminOrderListItem,
  AdminOrdersSummary,
} from '@sofia/shared-types';

const USD_TO_VND_RATE = 25400;

export const PaymentOrdersLogView: React.FC = () => {
  const { t, locale } = useI18n();

  // Filters & State
  const [gatewayFilter, setGatewayFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const pageSize = 20;

  // Selected Order Modal
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderListItem | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Format currency
  const formatCurrency = (usdAmount: number) => {
    const vnd = Math.round(usdAmount * USD_TO_VND_RATE);
    return (
      <div className="flex flex-col text-xs leading-tight">
        <span className="font-semibold text-foreground">
          ${usdAmount.toFixed(2)}
        </span>
        <span className="text-[10px] text-foreground-muted font-mono">
          (~{vnd.toLocaleString('vi-VN')} ₫)
        </span>
      </div>
    );
  };

  // Format creation timestamp
  const formatDate = (isoString: string) => {
    try {
      const d = new Date(isoString);
      return (
        <div className="flex flex-col text-xs">
          <span className="text-foreground font-mono">
            {d.toLocaleDateString(locale === 'vi' ? 'vi-VN' : 'en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span className="text-[10px] text-foreground-muted font-mono">
            {d.toLocaleTimeString(locale === 'vi' ? 'vi-VN' : 'en-US', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
            })}
          </span>
        </div>
      );
    } catch {
      return isoString;
    }
  };

  // Gateway Badge Renderer
  const renderGatewayBadge = (code: string) => {
    switch (code) {
      case 'stripe':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-primary/30 bg-primary/10 text-primary">
            <CreditCard className="w-3.5 h-3.5" />
            <span>Stripe</span>
          </span>
        );
      case 'paypal':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-cyan-500/30 bg-cyan-500/10 text-cyan-500">
            <Globe className="w-3.5 h-3.5" />
            <span>PayPal</span>
          </span>
        );
      case 'paddle':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-emerald-500/30 bg-emerald-500/10 text-emerald-500">
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Paddle MoR</span>
          </span>
        );
      case 'bank_transfer':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-amber-500/30 bg-amber-500/10 text-amber-500">
            <Building2 className="w-3.5 h-3.5" />
            <span>Bank Wire</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono border border-surface-border text-foreground-muted">
            {code}
          </span>
        );
    }
  };

  // Status Badge Renderer
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-emerald-500/30 bg-emerald-500/10 text-emerald-500">
            <CheckCircle2 className="w-3 h-3" />
            <span>{t('common.completed')}</span>
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-amber-500/30 bg-amber-500/10 text-amber-500">
            <Clock className="w-3 h-3" />
            <span>{t('common.pending')}</span>
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold border border-rose-500/30 bg-rose-500/10 text-rose-500">
            <XCircle className="w-3 h-3" />
            <span>{t('common.failed')}</span>
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border border-surface-border text-foreground-muted">
            {status}
          </span>
        );
    }
  };

  // Fetch orders from API Worker
  const { data, isLoading, isFetching, refetch } = useQuery<{
    success: boolean;
    data: AdminOrderListItem[];
    total: number;
    summary: AdminOrdersSummary;
  }>({
    queryKey: ['admin-payment-orders-log', gatewayFilter, statusFilter, searchQuery, page],
    queryFn: async () => {
      return adminClient.get('/api/v1/admin/orders', {
        status: statusFilter,
        gatewayCode: gatewayFilter,
        search: searchQuery || undefined,
        page,
        limit: pageSize,
      });
    },
  });

  const orders = data?.data || [];
  const total = data?.total || 0;
  const summary = data?.summary || {
    pendingCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    totalPendingAmountUsd: 0,
    totalApprovedAmountUsd: 0,
  };

  // Column definitions for DataTable
  const columns: ColumnDef<AdminOrderListItem>[] = useMemo(
    () => [
      {
        id: 'orderId',
        header: t('admin.orderLogs.colOrderId'),
        accessorKey: 'id',
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5 font-mono text-xs">
            <span className="font-semibold text-foreground">{row.id}</span>
            <button
              type="button"
              onClick={() => handleCopy(row.id, `id_${row.id}`)}
              className="text-foreground-muted hover:text-foreground transition-colors p-0.5"
              title="Copy Order ID"
            >
              {copiedId === `id_${row.id}` ? (
                <Check className="w-3 h-3 text-emerald-500" />
              ) : (
                <Copy className="w-3 h-3" />
              )}
            </button>
          </div>
        ),
      },
      {
        id: 'customer',
        header: t('admin.orderLogs.colCustomer'),
        accessorFn: (row) => `${row.workspaceName} ${row.userEmail}`,
        cell: ({ row }) => (
          <div className="flex flex-col text-xs leading-tight max-w-[180px]">
            <span className="font-semibold text-foreground truncate">
              {row.workspaceName || 'Personal Workspace'}
            </span>
            <span className="text-[10px] text-foreground-muted truncate">
              {row.userEmail || 'N/A'}
            </span>
          </div>
        ),
      },
      {
        id: 'item',
        header: t('admin.orderLogs.colItem'),
        accessorKey: 'planName',
        cell: ({ row }) => (
          <div className="flex flex-col text-xs leading-tight">
            <span className="font-medium text-foreground">
              {row.planName || row.planId}
            </span>
            {row.creditsIncluded ? (
              <span className="text-[10px] text-primary font-mono font-semibold">
                +{row.creditsIncluded.toLocaleString()} credits
              </span>
            ) : null}
          </div>
        ),
      },
      {
        id: 'amount',
        header: t('admin.orderLogs.colAmount'),
        accessorKey: 'amountPaid',
        cell: ({ row }) => formatCurrency(row.amountPaid),
      },
      {
        id: 'gateway',
        header: t('admin.orderLogs.colGateway'),
        accessorKey: 'gatewayCode',
        cell: ({ row }) => renderGatewayBadge(row.gatewayCode),
      },
      {
        id: 'txId',
        header: t('admin.orderLogs.colTxId'),
        accessorKey: 'gatewayTransactionId',
        cell: ({ row }) => {
          const tx = row.gatewayTransactionId;
          if (!tx) {
            return <span className="text-[10px] text-foreground-muted italic">pending</span>;
          }
          return (
            <div className="flex items-center gap-1 font-mono text-[11px] text-foreground-muted max-w-[140px]">
              <span className="truncate">{tx}</span>
              <button
                type="button"
                onClick={() => handleCopy(tx, `tx_${tx}`)}
                className="hover:text-foreground p-0.5 flex-shrink-0"
              >
                {copiedId === `tx_${tx}` ? (
                  <Check className="w-3 h-3 text-emerald-500" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            </div>
          );
        },
      },
      {
        id: 'status',
        header: t('admin.orderLogs.colStatus'),
        accessorKey: 'status',
        cell: ({ row }) => renderStatusBadge(row.status),
      },
      {
        id: 'createdAt',
        header: t('admin.orderLogs.colDate'),
        accessorKey: 'createdAt',
        cell: ({ row }) => formatDate(row.createdAt),
      },
      {
        id: 'actions',
        header: t('admin.orderLogs.colActions'),
        cell: ({ row }) => (
          <button
            type="button"
            onClick={() => setSelectedOrder(row)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-surface-border bg-surface hover:bg-muted text-xs font-medium text-foreground transition-colors"
          >
            <Eye className="w-3.5 h-3.5 text-primary" />
            <span>{t('admin.orderLogs.viewDetails')}</span>
          </button>
        ),
      },
    ],
    [locale, copiedId]
  );

  // Handle Export CSV
  const handleExportCsv = () => {
    if (!orders.length) return;
    exportToCsv(columns, orders, `sophia-orders-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  return (
    <div className="space-y-6 pb-12 max-w-7xl mx-auto">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-surface-border">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2.5">
            <Receipt className="w-6 h-6 text-primary" />
            {t('admin.orderLogs.title')}
          </h1>
          <p className="text-xs text-foreground-muted mt-1">
            {t('admin.orderLogs.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-surface-border bg-surface text-foreground hover:bg-muted text-xs font-medium transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            <span>{t('common.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={!orders.length}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-fg hover:bg-primary-hover active:scale-[0.98] text-xs font-semibold shadow-md transition-all disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            <span>{t('admin.orderLogs.exportCsv')}</span>
          </button>
        </div>
      </div>

      {/* KPI Financial Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Volume */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.orderLogs.kpiTotalVolume')}</span>
            <div className="w-8 h-8 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
              <DollarSign className="w-4 h-4" />
            </div>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-xl font-heading font-bold text-foreground">
              ${(summary.totalApprovedAmountUsd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
            <span className="text-xs text-foreground-muted font-mono">
              (~{(Math.round((summary.totalApprovedAmountUsd || 0) * USD_TO_VND_RATE)).toLocaleString()} ₫)
            </span>
          </div>
        </div>

        {/* Paid Orders */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.orderLogs.kpiPaidOrders')}</span>
            <div className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl font-heading font-bold text-emerald-500">
            {summary.approvedCount || 0}
          </div>
        </div>

        {/* Pending Review */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.orderLogs.kpiPendingOrders')}</span>
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-500 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl font-heading font-bold text-amber-500">
            {summary.pendingCount || 0}
          </div>
        </div>

        {/* Failed / Refunded */}
        <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm space-y-2">
          <div className="flex items-center justify-between text-foreground-muted">
            <span className="text-xs font-medium">{t('admin.orderLogs.kpiFailedOrders')}</span>
            <div className="w-8 h-8 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 flex items-center justify-center">
              <XCircle className="w-4 h-4" />
            </div>
          </div>
          <div className="text-xl font-heading font-bold text-rose-500">
            {summary.rejectedCount || 0}
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="p-4 rounded-2xl border border-surface-border bg-surface shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Search */}
        <div className="relative w-full md:w-96">
          <Search className="w-4 h-4 text-foreground-muted absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder={t('admin.orderLogs.searchPlaceholder')}
            className="w-full pl-9 pr-3.5 py-2 rounded-xl border border-surface-border bg-surface-base text-foreground text-xs focus:outline-none focus:border-primary transition-colors"
          />
        </div>

        {/* Gateway & Status Selectors */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          {/* Gateway Filter */}
          <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
            <span className="text-[11px] font-semibold">{t('admin.orderLogs.filterGateway')}:</span>
            <select
              value={gatewayFilter}
              onChange={(e) => {
                setGatewayFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1.5 rounded-xl border border-surface-border bg-surface-base text-foreground text-xs font-medium focus:outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">{t('common.all')}</option>
              <option value="stripe">Stripe</option>
              <option value="paypal">PayPal</option>
              <option value="paddle">Paddle MoR</option>
              <option value="bank_transfer">Bank Transfer (VietQR)</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="flex items-center gap-1.5 text-xs text-foreground-muted">
            <span className="text-[11px] font-semibold">{t('admin.orderLogs.filterStatus')}:</span>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-1.5 rounded-xl border border-surface-border bg-surface-base text-foreground text-xs font-medium focus:outline-none focus:border-primary transition-colors cursor-pointer"
            >
              <option value="all">{t('common.all')}</option>
              <option value="paid">{t('common.completed')}</option>
              <option value="pending">{t('common.pending')}</option>
              <option value="failed">{t('common.failed')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Orders Data Table */}
      <DataTable
        columns={columns}
        data={orders}
        isLoading={isLoading}
        totalCount={total}
        pageIndex={page - 1}
        pageSize={pageSize}
        onPageChange={(p) => setPage(p + 1)}
        isServerSide={true}
        enableSearch={false}
        emptyTitle={t('admin.orderLogs.emptyTitle') || 'No Orders Recorded'}
        emptyDescription={t('admin.orderLogs.emptyDescription') || 'When subscribers purchase plans or topup credit packs, payment transaction logs will be listed here.'}
      />

      {/* ========================================================= */}
      {/* Order Detail Modal */}
      {/* ========================================================= */}
      {selectedOrder && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) setSelectedOrder(null);
          }}
        >
          <div className="w-full max-w-lg rounded-2xl border border-surface-border bg-surface p-6 shadow-2xl animate-scale-in space-y-5">
            <div className="flex items-center justify-between border-b border-surface-border pb-4">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-sm text-foreground">
                    {t('admin.orderLogs.orderDetailTitle')}
                  </h3>
                  <code className="text-[11px] font-mono text-foreground-muted">
                    {selectedOrder.id}
                  </code>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="p-1 rounded-lg text-foreground-muted hover:text-foreground hover:bg-muted transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl border border-surface-border bg-muted/30">
                <div>
                  <span className="text-[10px] text-foreground-muted block">
                    {t('admin.orderLogs.colCustomer')}
                  </span>
                  <span className="font-semibold text-foreground">
                    {selectedOrder.workspaceName || 'Personal Workspace'}
                  </span>
                  <span className="text-[11px] text-foreground-muted block">
                    {selectedOrder.userEmail}
                  </span>
                </div>

                <div>
                  <span className="text-[10px] text-foreground-muted block">
                    {t('admin.orderLogs.colAmount')}
                  </span>
                  <span className="text-base font-heading font-bold text-primary">
                    ${selectedOrder.amountPaid.toFixed(2)}
                  </span>
                  <span className="text-[10px] text-foreground-muted block font-mono">
                    (~{(Math.round(selectedOrder.amountPaid * USD_TO_VND_RATE)).toLocaleString()} ₫)
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 rounded-xl border border-surface-border">
                <div>
                  <span className="text-[10px] text-foreground-muted block">
                    {t('admin.orderLogs.colGateway')}
                  </span>
                  <div className="mt-1">{renderGatewayBadge(selectedOrder.gatewayCode)}</div>
                </div>

                <div>
                  <span className="text-[10px] text-foreground-muted block">
                    {t('admin.orderLogs.colStatus')}
                  </span>
                  <div className="mt-1">{renderStatusBadge(selectedOrder.status)}</div>
                </div>
              </div>

              <div className="p-3 rounded-xl border border-surface-border space-y-1">
                <span className="text-[10px] text-foreground-muted block">
                  {t('admin.orderLogs.gatewayTransactionId')}
                </span>
                <code className="block text-[11px] font-mono text-foreground break-all">
                  {selectedOrder.gatewayTransactionId || 'None (pending)'}
                </code>
              </div>

              {/* Proof image preview if available */}
              {selectedOrder.proofUrl || selectedOrder.proofFileR2 ? (
                <div className="p-3 rounded-xl border border-surface-border space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-semibold text-foreground-muted">
                      {t('admin.orderLogs.proofFile')}
                    </span>
                    {(selectedOrder.proofUrl || selectedOrder.proofFileR2) && (
                      <a
                        href={selectedOrder.proofUrl || selectedOrder.proofFileR2!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-[11px] text-primary hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span>{t('admin.orderLogs.viewReceipt')}</span>
                      </a>
                    )}
                  </div>
                  <div className="rounded-xl overflow-hidden border border-surface-border max-h-48 bg-black/40 flex items-center justify-center">
                    <img
                      src={selectedOrder.proofUrl || selectedOrder.proofFileR2!}
                      alt="Bank Transfer Receipt"
                      className="max-h-48 object-contain"
                    />
                  </div>
                </div>
              ) : null}
            </div>

            <div className="flex justify-end pt-2 border-t border-surface-border">
              <button
                type="button"
                onClick={() => setSelectedOrder(null)}
                className="px-4 py-2 rounded-xl border border-surface-border text-xs font-medium text-foreground hover:bg-muted transition-colors"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
